import { useMemo } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Fonction asynchrone pour récupérer les paramètres de filtrage depuis la base de données
 * @returns Les paramètres de filtrage avec tenant_id résolu depuis la DB
 */
export const getDomainFilterParamsAsync = async () => {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : null;
  
  if (!currentDomain) {
    return {
      domain: null,
      tenantId: '2', // Fallback par défaut
      hasValidDomain: false
    };
  }

  try {
    // Résoudre le tenant_id depuis la base de données
    const { data: resolvedData, error: resolveError } = await supabase
      .rpc('resolve_tenant_by_domain', { domain_input: currentDomain });

    if (!resolveError && resolvedData && resolvedData.length > 0) {
      const resolved = resolvedData[0];
      return {
        domain: currentDomain,
        tenantId: resolved.tenant_id?.toString() || '2',
        hasValidDomain: true
      };
    }
  } catch (error) {
    console.warn('Erreur lors de la résolution du tenant par domaine:', error);
  }

  // Fallback: essayer le cache localStorage
  let tenantId = '2';
  if (typeof window !== 'undefined') {
    try {
      const cachedTenant = localStorage.getItem('tenant_cache');
      if (cachedTenant) {
        const parsed = JSON.parse(cachedTenant);
        if (parsed.tenantId) {
          tenantId = parsed.tenantId;
        }
      }
    } catch (error) {
      console.warn('Erreur lors de la récupération du tenant depuis le cache:', error);
    }
  }

  return {
    domain: currentDomain,
    tenantId,
    hasValidDomain: true
  };
};

/**
 * Fonction utilitaire pour créer des filtres domain/tenant_id (utilisable dans les services)
 * @deprecated Utiliser getDomainFilterParamsAsync() pour une résolution fiable
 * @returns Les paramètres de filtrage
 */
export const getDomainFilterParams = () => {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : null;
  
  // Récupérer le tenant ID depuis le cache ou résolution par domaine
  let tenantId = import.meta.env.VITE_TENANT_ID || '2';
  
  // Essayer de récupérer le tenant ID résolu depuis le localStorage
  if (typeof window !== 'undefined') {
    try {
      const cachedTenant = localStorage.getItem('tenant_cache');
      if (cachedTenant) {
        const parsed = JSON.parse(cachedTenant);
        if (parsed.tenantId) {
          tenantId = parsed.tenantId;
        }
      }
    } catch (error) {
      console.warn('Erreur lors de la récupération du tenant depuis le cache:', error);
    }
  }

  return {
    domain: currentDomain,
    tenantId,
    hasValidDomain: !!currentDomain
  };
};

/**
 * Applique le filtre domain/tenant_id à une requête Supabase (utilisable dans les services)
 * @param query - La requête Supabase de base
 * @returns La requête modifiée avec les filtres appropriés
 */
export const applyDomainFilterToQuery = (query: any) => {
  const { domain, tenantId, hasValidDomain } = getDomainFilterParams();

  if (hasValidDomain) {
    // Priorité absolue: Filtrer UNIQUEMENT par domaine
    return query.eq('domain', domain);
  } else {
    // Fallback: Utiliser seulement tenant_id si pas de domaine
    return query.eq('tenant_id', tenantId);
  }
};

/**
 * Hook React pour utiliser les filtres domain/tenant_id dans les composants
 */
export const useDomainQuery = () => {
  const currentDomain = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.location.hostname;
    }
    return null;
  }, []);

  const tenantId = import.meta.env.VITE_TENANT_ID || '2';

  /**
   * Crée une requête avec fallback domain -> tenant_id
   * @param query - La requête Supabase de base
   * @returns La requête modifiée avec les filtres appropriés
   */
  const applyDomainFilter = (query: any) => {
    if (currentDomain) {
      // Priorité 1: Filtrer par domain si disponible
      return query.or(`domain.eq.${currentDomain},and(domain.is.null,tenant_id.eq.${tenantId})`);
    } else {
      // Fallback: Utiliser seulement tenant_id
      return query.eq('tenant_id', tenantId);
    }
  };

  /**
   * Applique le filtre seulement par domaine (sans fallback tenant_id)
   * @param query - La requête Supabase de base
   * @returns La requête modifiée avec le filtre domaine uniquement
   */
  const applyDomainOnlyFilter = (query: any) => {
    if (currentDomain) {
      // Filtrer seulement par domaine, sans fallback
      return query.eq('domain', currentDomain);
    } else {
      // Si pas de domaine valide, retourner une requête qui ne retourne rien
      return query.eq('domain', 'no-domain-match');
    }
  };

  /**
   * Retourne les paramètres de filtrage pour les requêtes manuelles
   */
  const getFilterParams = () => ({
    domain: currentDomain,
    tenantId,
    hasValidDomain: !!currentDomain
  });

  return {
    applyDomainFilter,
    applyDomainOnlyFilter,
    getFilterParams,
    currentDomain,
    tenantId
  };
};

/**
 * Applique le filtre seulement par domaine (sans fallback tenant_id) - version utilitaire
 * @param query - La requête Supabase de base
 * @returns La requête modifiée avec le filtre domaine uniquement
 */
export const applyDomainOnlyFilterToQuery = (query: any) => {
  const { domain, hasValidDomain } = getDomainFilterParams();

  if (hasValidDomain) {
    // Filtrer seulement par domaine, sans fallback
    return query.eq('domain', domain);
  } else {
    // Si pas de domaine valide, retourner une requête qui ne retourne rien
    return query.eq('domain', 'no-domain-match');
  }
};
